import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "urql";
import { Link, useLocation, useParams } from "wouter";

import { ReactTable } from "../../../../components/src/react-table/ReactTable";
import { createReactTableColumnHelper, ReactTableColumnDef } from "../../../../components/src/react-table/features";
import { DefaultColumnFilter } from "../../../../components/src/react-table/ReactTableFilters";
import { Button } from "../../../../components/src/Button";

import { T_ACCOUNT_CHART, UPSERT_T_ACCOUNT_CHART } from "../../../../graphql/src/taccountchart2/queries";

import { userHaveAccessTo } from "../../common/Permissions";
import { TAccountsForm } from "./TAccountsForm";
import { TAccountMappingsForm } from "./TAccountMappingsForm";
import { PermissionAssetEnum, PermissionTypeEnum } from "../../types.generated";
import type { UpsertTAccount, UpsertTAccountChart, UpsertTAccountChartInput, UpsertTAccountMapping } from "../../types.generated";

export function TAccountChartPage(): React.ReactElement {
    const { id } = useParams<{ id: string }>();
    const [{ fetching, error, data }, refetch] = useQuery<{ tAccountCharts2: UpsertTAccountChart[]; me: { roles: any[] } }>({
        query: T_ACCOUNT_CHART,
        variables: { id }
    });
    // one part is edited at a time, a save replaces the whole chart and the other part would be stale
    const [editing, setEditing] = useState<"tAccounts" | "tAccountMappings" | null>(null);
    const [{ fetching: savingNewVersion }, upsertTAccountChart] = useMutation(UPSERT_T_ACCOUNT_CHART);
    const [__, navigate] = useLocation();
    const [newVersionError, setNewVersionError] = useState<string | null>(null);

    // a link with a hash, e.g. #tAccountMappings from the accounting run page, scrolls to its section when the chart is loaded
    useEffect(() => {
        if (data && window.location.hash) document.getElementById(window.location.hash.slice(1))?.scrollIntoView();
    }, [data]);

    const tAccountColumns = useMemo<ReactTableColumnDef<UpsertTAccount>[]>(() => {
        const columnHelper = createReactTableColumnHelper<UpsertTAccount>();

        return [
            columnHelper.accessor("number", { header: "Number", size: 100, filter: DefaultColumnFilter }),
            columnHelper.accessor("type", { header: "Type", size: 100 }),
            columnHelper.accessor("description", { header: "Description", size: 250, filter: DefaultColumnFilter }),
            columnHelper.accessor("group1", { header: "Group 1", size: 100 }),
            columnHelper.accessor("group2", { header: "Group 2", size: 100 }),
            columnHelper.accessor("group3", { header: "Group 3", size: 100 }),
            columnHelper.accessor("group4", { header: "Group 4", size: 100 }),
            columnHelper.accessor("group5", { header: "Group 5", size: 100 })
        ];
    }, []);

    const tAccountMappingColumns = useMemo<ReactTableColumnDef<UpsertTAccountMapping>[]>(() => {
        const columnHelper = createReactTableColumnHelper<UpsertTAccountMapping>();

        return [
            columnHelper.accessor("name", { header: "Name", size: 200, filter: DefaultColumnFilter }),
            columnHelper.accessor("selector", {
                header: "Selector",
                cell: (cellProps) => {
                    const selector = cellProps.getValue();
                    // only the criteria that restrict the mapping, empty criteria match everything
                    const criteria = [
                        { label: "Instruments", values: selector.instruments },
                        { label: "Currencies", values: selector.currencies },
                        { label: "Model types", values: selector.modelTypes },
                        { label: "CIC types", values: selector.cicTypes }
                    ].filter((criterion) => criterion.values && criterion.values.length > 0);
                    return (
                        <div>
                            {criteria.map((criterion) => (
                                <div key={criterion.label}>
                                    {criterion.label}: {criterion.values.join(", ")}
                                </div>
                            ))}
                        </div>
                    );
                },
                size: 300
            }),
            columnHelper.accessor("values", {
                header: "Debit / credit",
                cell: (cellProps) => {
                    const values = cellProps.getValue() ?? [];
                    return (
                        <div>
                            {values.map((value) => (
                                <div key={value.accountingTransactionType}>
                                    {value.accountingTransactionType}: {value.debit} / {value.credit}
                                </div>
                            ))}
                        </div>
                    );
                },
                size: 300
            })
        ];
    }, []);

    if (fetching) return <p>Loading</p>;
    if (error) return <p>error: {JSON.stringify(error, null, 2)}</p>;

    const tAccountChart = data && data.tAccountCharts2 && data.tAccountCharts2.length > 0 ? data.tAccountCharts2[0] : null;
    if (!tAccountChart) {
        return (
            <div>
                <p>T-account chart {id} not found</p>
                <Link to="/taccountcharts2">T-account charts</Link>
            </div>
        );
    }

    const canWrite =
        data.me && userHaveAccessTo(tAccountChart.clientId, PermissionAssetEnum.Accounting, data.me.roles, PermissionTypeEnum.ReadWrite);
    // a locked chart is used by an accounting run and can not be changed, changes go into a new version
    const canEdit = canWrite && !tAccountChart.locked;

    return (
        <div>
            <Link to="/taccountcharts2">T-account charts</Link>
            <h4>
                {tAccountChart.client?.name} - {tAccountChart.name} (version {tAccountChart.version})
            </h4>
            <div className="mb-4">
                <div>Locked: {tAccountChart.locked ? "Yes" : "No"}</div>
                <div>
                    Created: {tAccountChart.createTimestamp} by {tAccountChart.createUserInfo?.name}
                </div>
                <div>
                    Updated: {tAccountChart.updateTimestamp} by {tAccountChart.updateUserInfo?.name}
                </div>
            </div>
            {canWrite && tAccountChart.locked ? (
                <div className="mb-4">
                    <Button
                        type="button"
                        size="sm"
                        disabled={savingNewVersion}
                        onClick={async () => {
                            setNewVersionError(null);
                            // a copy without _id and version is created as the next version, unlocked and editable
                            const input: UpsertTAccountChartInput = JSON.parse(
                                JSON.stringify(
                                    {
                                        clientId: tAccountChart.clientId,
                                        name: tAccountChart.name,
                                        tAccounts: tAccountChart.tAccounts ?? [],
                                        tAccountMappings: tAccountChart.tAccountMappings ?? []
                                    },
                                    (key, v) => (key === "__typename" ? undefined : v)
                                )
                            );
                            const result = await upsertTAccountChart({ input });
                            if (result.error) {
                                setNewVersionError(
                                    result.error.graphQLErrors.length > 0 ? result.error.graphQLErrors[0].message : result.error.message
                                );
                            } else {
                                navigate("/taccountcharts2/" + result.data.upsertTAccountChart2._id);
                            }
                        }}
                    >
                        Save as new version
                    </Button>
                    {newVersionError ? <div className="form-field-error">{newVersionError}</div> : null}
                </div>
            ) : null}
            <h5>T-accounts ({(tAccountChart.tAccounts ?? []).length})</h5>
            <div className="mb-4">
                {editing === "tAccounts" ? (
                    <TAccountsForm
                        tAccountChart={tAccountChart}
                        onDone={(saved) => {
                            setEditing(null);
                            if (saved) refetch({ requestPolicy: "network-only" });
                        }}
                    />
                ) : (
                    <div>
                        {canEdit && editing === null ? (
                            <Button type="button" size="sm" className="mb-2" onClick={() => setEditing("tAccounts")}>
                                Edit t-accounts
                            </Button>
                        ) : null}
                        <ReactTable columns={tAccountColumns} data={tAccountChart.tAccounts ?? []} />
                    </div>
                )}
            </div>
            <h5 id="tAccountMappings">T-account mappings ({(tAccountChart.tAccountMappings ?? []).length})</h5>
            <p>Mappings are applied in order, a later matching mapping overrides an earlier one.</p>
            {editing === "tAccountMappings" ? (
                <TAccountMappingsForm
                    tAccountChart={tAccountChart}
                    onDone={(saved) => {
                        setEditing(null);
                        if (saved) refetch({ requestPolicy: "network-only" });
                    }}
                />
            ) : (
                <div>
                    {canEdit && editing === null ? (
                        <Button type="button" size="sm" className="mb-2" onClick={() => setEditing("tAccountMappings")}>
                            Edit mappings
                        </Button>
                    ) : null}
                    <ReactTable columns={tAccountMappingColumns} data={tAccountChart.tAccountMappings ?? []} />
                </div>
            )}
        </div>
    );
}
